<template>
  <v-form ref="form" v-model="valid" v-if="formData && isEditable">
    <v-card>
      <v-card-text>
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              v-if="displayField('name')"
              v-model="formData.name"
              :label="$t('fundBaseForm.name.label')"
              :rules="[(v) => !!v || $t('validation.required')]"
              required
            >
              <template #prepend>
                <v-tooltip bottom max-width="300">
                  <template #activator="{ on }">
                    <v-icon color="primary" dark v-on="on">mdi-help</v-icon>
                  </template>
                  <span>{{ $t("fundBaseForm.name.help") }}</span>
                </v-tooltip>
              </template>
            </v-text-field>

            <v-text-field
              v-if="displayField('assetClassesComment')"
              v-model="formData.assetClassesComment"
              :label="$t('fundBaseForm.assetClassesComment.label')"
              required
            >
              <template #prepend>
                <v-tooltip bottom max-width="300">
                  <template #activator="{ on }">
                    <v-icon color="primary" dark v-on="on">mdi-help</v-icon>
                  </template>
                  <span>{{ $t("fundBaseForm.assetClassesComment.help") }}</span>
                </v-tooltip>
              </template>
            </v-text-field>

            <v-text-field
              v-if="displayField('assetManager')"
              v-model="formData.assetManager"
              :label="$t('fundBaseForm.assetManager.label')"
              required
            >
              <template #prepend>
                <v-tooltip bottom max-width="300">
                  <template #activator="{ on }">
                    <v-icon color="primary" dark v-on="on">mdi-help</v-icon>
                  </template>
                  <span>{{ $t("fundBaseForm.assetManager.help") }}</span>
                </v-tooltip>
              </template>
            </v-text-field>

            <v-text-field
              v-if="displayField('dependance')"
              v-model="formData.dependance"
              :label="$t('fundBaseForm.dependance.label')"
              required
            >
              <template #prepend>
                <v-tooltip bottom max-width="300">
                  <template #activator="{ on }">
                    <v-icon color="primary" dark v-on="on">mdi-help</v-icon>
                  </template>
                  <span>{{ $t("fundBaseForm.dependance.help") }}</span>
                </v-tooltip>
              </template>
            </v-text-field>

            <v-combobox
              v-if="displayField('isins')"
              v-model="formData.isins"
              :label="$t('fundBaseForm.isins.label')"
              :rules="[(v) => v.length > 0 || $t('validation.required')]"
              multiple
              small-chips
              clearable
              deletable-chips
            >
              <template #prepend>
                <v-tooltip bottom max-width="300">
                  <template #activator="{ on }">
                    <v-icon color="primary" dark v-on="on">mdi-help</v-icon>
                  </template>
                  <span>{{ $t("fundBaseForm.isins.help") }}</span>
                </v-tooltip>
              </template>
            </v-combobox>

            <v-text-field
              v-if="displayField('benchmark')"
              v-model="formData.benchmark"
              :label="$t('fundBaseForm.benchmark.label')"
              required
            >
              <template #prepend>
                <v-tooltip bottom max-width="300">
                  <template #activator="{ on }">
                    <v-icon color="primary" dark v-on="on">mdi-help</v-icon>
                  </template>
                  <span>{{ $t("fundBaseForm.benchmark.help") }}</span>
                </v-tooltip>
              </template>
            </v-text-field>

            <v-autocomplete
              v-if="displayField('investmentApproaches')"
              v-model="formData.investmentApproaches"
              :items="investmentApproaches"
              :label="$t('fundBaseForm.investmentApproaches.label')"
              :rules="[(v) => v.length > 0 || $t('validation.required')]"
              multiple
              small-chips
              deletable-chips
            >
              <template #prepend>
                <v-tooltip bottom max-width="300">
                  <template #activator="{ on }">
                    <v-icon color="primary" dark v-on="on">mdi-help</v-icon>
                  </template>
                  <span>{{ $t("fundBaseForm.investmentApproaches.help") }}</span>
                </v-tooltip>
              </template>
            </v-autocomplete>

            <v-select
              v-if="displayField('fundType')"
              v-model="formData.fundType"
              :items="fundTypes"
              :label="$t('fundBaseForm.fundType.label')"
              :rules="[(v) => !!v || $t('validation.required')]"
            >
              <template #prepend>
                <v-tooltip bottom max-width="300">
                  <template #activator="{ on }">
                    <v-icon color="primary" dark v-on="on">mdi-help</v-icon>
                  </template>
                  <span>{{ $t("fundBaseForm.fundType.help") }}</span>
                </v-tooltip>
              </template>
            </v-select>

            <v-checkbox
              v-if="displayField('privateInvestors')"
              v-model="formData.privateInvestors"
              :label="$t('fundBaseForm.privateInvestors.label')"
            >
              <template #prepend>
                <v-tooltip bottom max-width="300">
                  <template #activator="{ on }">
                    <v-icon color="primary" dark v-on="on">mdi-help</v-icon>
                  </template>
                  <span>{{ $t("fundBaseForm.privateInvestors.help") }}</span>
                </v-tooltip>
              </template>
            </v-checkbox>

            <v-autocomplete
              v-if="displayField('tranches')"
              v-model="formData.tranches"
              :items="tranches"
              :label="$t('fundBaseForm.tranches.label')"
              multiple
              small-chips
              deletable-chips
            >
              <template #prepend>
                <v-tooltip bottom max-width="300">
                  <template #activator="{ on }">
                    <v-icon color="primary" dark v-on="on">mdi-help</v-icon>
                  </template>
                  <span>{{ $t("fundBaseForm.tranches.help") }}</span>
                </v-tooltip>
              </template>
            </v-autocomplete>

            <v-text-field
              v-if="displayField('tranchesComment')"
              v-model="formData.tranchesComment"
              :label="$t('fundBaseForm.tranchesComment.label')"
              required
            >
              <template #prepend>
                <v-tooltip bottom max-width="300">
                  <template #activator="{ on }">
                    <v-icon color="primary" dark v-on="on">mdi-help</v-icon>
                  </template>
                  <span>{{ $t("fundBaseForm.tranchesComment.help") }}</span>
                </v-tooltip>
              </template>
            </v-text-field>

            <v-checkbox
              v-if="displayField('performanceFee')"
              v-model="formData.performanceFee"
              :label="$t('fundBaseForm.performanceFee.label')"
            >
              <template #prepend>
                <v-tooltip bottom max-width="300">
                  <template #activator="{ on }">
                    <v-icon color="primary" dark v-on="on">mdi-help</v-icon>
                  </template>
                  <span>{{ $t("fundBaseForm.performanceFee.help") }}</span>
                </v-tooltip>
              </template>
            </v-checkbox>

            <v-checkbox v-if="displayField('rter')" v-model="formData.rter" :label="$t('fundBaseForm.rter.label')">
              <template #prepend>
                <v-tooltip bottom max-width="300">
                  <template #activator="{ on }">
                    <v-icon color="primary" dark v-on="on">mdi-help</v-icon>
                  </template>
                  <span>{{ $t("fundBaseForm.rter.help") }}</span>
                </v-tooltip>
              </template>
            </v-checkbox>
          </v-col>

          <v-col cols="12" md="6">
            <v-autocomplete
              v-if="displayField('salesCountries')"
              v-model="formData.salesCountries"
              :items="countries"
              :label="$t('fundBaseForm.salesCountries.label')"
              :hint="$t('fundBaseForm.salesCountries.hint')"
              persistent-hint
              :rules="[(v) => v.length > 0 || $t('validation.required')]"
              multiple
              small-chips
              deletable-chips
            >
              <template #prepend>
                <v-tooltip bottom max-width="300">
                  <template #activator="{ on }">
                    <v-icon color="primary" dark v-on="on">mdi-help</v-icon>
                  </template>
                  <span>{{ $t("fundBaseForm.salesCountries.help") }}</span>
                </v-tooltip>
              </template>
            </v-autocomplete>

            <v-select
              v-if="displayField('investmentRegion')"
              v-model="formData.investmentRegion"
              :items="investmentRegions"
              :label="$t('fundBaseForm.investmentRegion.label')"
              :rules="[(v) => !!v || $t('validation.required')]"
            >
              <template #prepend>
                <v-tooltip bottom max-width="300">
                  <template #activator="{ on }">
                    <v-icon color="primary" dark v-on="on">mdi-help</v-icon>
                  </template>
                  <span>{{ $t("fundBaseForm.investmentRegion.help") }}</span>
                </v-tooltip>
              </template>
            </v-select>

            <v-text-field
              v-if="displayField('investmentRegionComment')"
              v-model="formData.investmentRegionComment"
              :label="$t('fundBaseForm.investmentRegionComment.label')"
              required
            >
              <template #prepend>
                <v-tooltip bottom max-width="300">
                  <template #activator="{ on }">
                    <v-icon color="primary" dark v-on="on">mdi-help</v-icon>
                  </template>
                  <span>{{ $t("fundBaseForm.investmentRegionComment.help") }}</span>
                </v-tooltip>
              </template>
            </v-text-field>

            <v-text-field
              v-if="displayField('volume')"
              v-model="formData.volume"
              :label="
                $t('fundBaseForm.volume.label', {
                  year: new Date().getFullYear(),
                })
              "
              :rules="[(v) => !!v || $t('validation.required')]"
              required
              type="number"
            >
              <template #prepend>
                <v-tooltip bottom max-width="300">
                  <template #activator="{ on }">
                    <v-icon color="primary" dark v-on="on">mdi-help</v-icon>
                  </template>
                  <span>{{ $t("fundBaseForm.volume.help") }}</span>
                </v-tooltip>
              </template>
            </v-text-field>

            <v-autocomplete
              v-if="displayField('currency')"
              v-model="formData.currency"
              :items="currencies"
              :label="$t('fundBaseForm.currency.label')"
              :rules="[(v) => !!v || $t('validation.required')]"
            >
              <template #prepend>
                <v-tooltip bottom max-width="300">
                  <template #activator="{ on }">
                    <v-icon color="primary" dark v-on="on">mdi-help</v-icon>
                  </template>
                  <span>{{ $t("fundBaseForm.currency.help") }}</span>
                </v-tooltip>
              </template>
            </v-autocomplete>

            <v-menu
              v-if="displayField('launchDate')"
              ref="menu"
              v-model="menu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template #activator="{ on }">
                <v-text-field
                  v-model="formData.launchDate"
                  :label="$t('fundBaseForm.launchDate.label')"
                  :hint="$t('fundBaseForm.launchDate.hint')"
                  :rules="[(v) => !!v || $t('validation.required')]"
                  readonly
                  v-on="on"
                >
                  <template #prepend>
                    <v-tooltip bottom max-width="300">
                      <template #activator="{ on }">
                        <v-icon color="primary" dark v-on="on">mdi-help</v-icon>
                      </template>
                      <span>{{ $t("fundBaseForm.launchDate.help") }}</span>
                    </v-tooltip>
                  </template>
                </v-text-field>
              </template>
              <v-date-picker v-model="formData.launchDate" no-title scrollable @input="menu = false"> </v-date-picker>
            </v-menu>

            <v-select
              v-if="displayField('language')"
              :items="languages"
              :label="$t('fundBaseForm.language.label')"
              :rules="[(v) => !!v || $t('validation.required')]"
              v-model="formData.language"
            >
              <template #prepend>
                <v-tooltip bottom max-width="300">
                  <template #activator="{ on }">
                    <v-icon color="primary" dark v-on="on">mdi-help</v-icon>
                  </template>
                  <span>{{ $t("fundBaseForm.language.help") }}</span>
                </v-tooltip>
              </template>
            </v-select>

            <v-select
              v-if="displayField('disclosure')"
              :items="disclosure"
              :label="$t('fundBaseForm.disclosure.label')"
              v-model="formData.disclosure"
              :rules="[(v) => !!v || $t('validation.required')]"
            >
              <template #prepend>
                <v-tooltip bottom max-width="300">
                  <template #activator="{ on }">
                    <v-icon color="primary" dark v-on="on">mdi-help</v-icon>
                  </template>
                  <span>{{ $t("fundBaseForm.disclosure.help") }}</span>
                </v-tooltip>
              </template>
            </v-select>

            <v-textarea
              v-if="displayField('disclosureComment')"
              v-model="formData.disclosureComment"
              :label="$t('fundBaseForm.disclosureComment.label')"
              :hint="$t('fundBaseForm.disclosureComment.hint')"
              :rules="[(v) => !v || v.length < 1000 || $t('validation.tooLong')]"
              counter="1000"
              auto-grow
            >
              <template #prepend>
                <v-tooltip bottom max-width="300">
                  <template #activator="{ on }">
                    <v-icon color="primary" dark v-on="on">mdi-help</v-icon>
                  </template>
                  <span>{{ $t("fundBaseForm.disclosureComment.help") }}</span>
                </v-tooltip>
              </template>
            </v-textarea>

            <v-textarea
              v-if="displayField('thirdPartyComment')"
              v-model="formData.thirdPartyComment"
              :label="$t('fundBaseForm.thirdPartyComment.label')"
              :hint="$t('fundBaseForm.thirdPartyComment.hint')"
              auto-grow
            >
              <template #prepend>
                <v-tooltip bottom max-width="300">
                  <template #activator="{ on }">
                    <v-icon color="primary" dark v-on="on">mdi-help</v-icon>
                  </template>
                  <span>{{ $t("fundBaseForm.thirdPartyComment.help") }}</span>
                </v-tooltip>
              </template>
            </v-textarea>

            <v-textarea
              v-if="displayField('comment')"
              v-model="formData.comment"
              :label="$t('fundBaseForm.comment.label')"
              :rules="[(v) => !v || v.length < 200 || $t('validation.tooLong')]"
              counter="200"
              auto-grow
            >
              <template #prepend>
                <v-tooltip bottom max-width="300">
                  <template #activator="{ on }">
                    <v-icon color="primary" dark v-on="on">mdi-help</v-icon>
                  </template>
                  <span>{{ $t("fundBaseForm.comment.help") }}</span>
                </v-tooltip>
              </template>
            </v-textarea>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions>
        <v-btn color="primary" @click="save" :loading="loading" :disabled="!valid">
          {{ $t("fundEditForm.save") }}
        </v-btn>

        <v-spacer />

        <v-btn :to="{ name: 'home' }" color="secondary">
          {{ $t("action.cancel") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-form>
</template>

<script>
import { pick, sortBy, prop } from "ramda";

import tranches from "@fng/common/tranches";
import countries from "@fng/common/countries";
import fundTypes from "@fng/common/fundTypes";
import disclosure from "@fng/common/disclosure";
import currencies from "@fng/common/currencies";
import fundIsEditable from "@fng/common/fundIsEditable";
import generateSelectItems from "@/utils/generateSelectItems";
import investmentRegions from "@fng/common/investmentRegions";
import FundBaseFormQuery from "@/gql/queries/FundBaseFormQuery.gql";
import investmentApproaches from "@fng/common/investmentApproaches";
import FundBaseFormMutation from "@/gql/mutations/FundBaseFormMutation.gql";

import { displayFundField } from "@fng/common/fund/displayFundField";

const languages = ["en", "de"];

export default {
  props: {
    id: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      formData: null,
      fund: null,
      valid: false,
      loading: false,
      menu: false,
    };
  },

  computed: {
    isEditable() {
      return fundIsEditable(this.fund, this.fund.auditPeriod);
    },

    investmentApproaches: generateSelectItems(investmentApproaches, "fund.investmentApproaches"),

    fundTypes: generateSelectItems(fundTypes, "fund.fundTypes"),

    tranches: generateSelectItems(tranches, "fund.tranches"),

    investmentRegions: generateSelectItems(investmentRegions, "fund.investmentRegions"),

    disclosure() {
      return sortBy(prop("text"), generateSelectItems(disclosure, "disclosure").apply(this));
    },

    countries() {
      return sortBy(prop("text"), generateSelectItems(countries, "countries").apply(this));
    },

    currencies() {
      return sortBy(prop("text"), generateSelectItems(currencies, "currencies").apply(this));
    },

    languages() {
      return sortBy(prop("text"), generateSelectItems(languages, "languages").apply(this));
    },
  },

  apollo: {
    fund: {
      query: FundBaseFormQuery,
      variables() {
        return {
          id: this.id,
        };
      },
    },
  },

  watch: {
    fund(val) {
      this.formData = {
        ...pick(
          [
            "name",
            "assetClasses",
            "assetClassesComment",
            "assetManager",
            "assetManagement",
            "dependance",
            "isins",
            "privateInvestors",
            "benchmark",
            "investmentApproaches",
            "fundType",
            "salesCountries",
            "investmentRegion",
            "investmentRegionComment",
            "volume",
            "currency",
            "launchDate",
            "tranches",
            "tranchesComment",
            "performanceFee",
            "rter",
            "disclosure",
            "state",
            "language",
            "referenceFund",
            "disclosureComment",
            "thirdPartyComment",
            "comment",
          ],
          val
        ),
        referenceFund: val.referenceFund ? val.referenceFund.id : null,
        launchDate: val.launchDate ? val.launchDate.substr(0, 10) : null,
      };
    },
  },

  methods: {
    displayField(field) {
      return displayFundField(this.formData, field);
    },
    async save() {
      if (!this.$refs.form.validate()) return;
      this.loading = true;
      await this.update();
      this.loading = false;
      this.$router.push({ name: "home" });
    },

    async update() {
      await this.$apollo.mutate({
        mutation: FundBaseFormMutation,
        variables: {
          id: this.fund.id,
          input: {
            ...this.formData,
            volume: parseFloat(this.formData.volume),
          },
        },
      });
    },
  },
};
</script>
